/**
 * Flatten widget → events for admin list/search (events live inside widgets, not standalone).
 */

export const EVENT_FILTER_OPTIONS = [
  { label: "Show all events", value: "all" },
  { label: "Only upcoming events", value: "upcoming" },
  { label: "Only past events", value: "past" },
  { label: "Only recurring events", value: "recurring" },
  { label: "Only published events", value: "published" },
  { label: "Only draft (hidden) events", value: "draft" },
  { label: "With registration form", value: "registration" },
];

function parseEventDate(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? null : d;
}

export function isEventRecurring(ev) {
  if (!ev) return false;
  if (ev.recurring === true) return true;
  const rule = String(ev.recurringRule || "").trim().toLowerCase();
  return rule && rule !== "none";
}

export function isEventUpcoming(ev, now = new Date()) {
  const end = parseEventDate(ev?.endAt);
  const start = parseEventDate(ev?.startAt);
  const ref = end || start;
  if (!ref) return true;
  return ref.getTime() >= now.getTime();
}

export function isEventPast(ev, now = new Date()) {
  const end = parseEventDate(ev?.endAt);
  const start = parseEventDate(ev?.startAt);
  const ref = end || start;
  if (!ref) return false;
  return ref.getTime() < now.getTime();
}

/** True if the shop has at least one saved calendar widget (setup guide step 1). */
export function hasAnyWidgets(widgets) {
  const list = Array.isArray(widgets) ? widgets : [];
  return list.some((widget) => widget && widget.id);
}

/** True if any active widget has at least one event. */
export function widgetsHaveCalendarEvents(widgets) {
  const list = Array.isArray(widgets) ? widgets : [];
  return list.some((widget) => {
    const events = Array.isArray(widget?.events) ? widget.events : [];
    return events.some((ev) => ev && ev.id);
  });
}

export function flattenWidgetsToEvents(widgets) {
  const list = Array.isArray(widgets) ? widgets : [];
  const rows = [];
  for (const widget of list) {
    if (!widget?.id) continue;
    const events = Array.isArray(widget.events) ? widget.events : [];
    for (const ev of events) {
      if (!ev?.id) continue;
      rows.push({
        eventId: ev.id,
        widgetId: widget.id,
        widgetName: widget.name || "Untitled widget",
        templateId: widget.templateId,
        title: ev.title || "Untitled event",
        startAt: ev.startAt || "",
        endAt: ev.endAt || "",
        hidden: Boolean(ev.hidden),
        recurring: isEventRecurring(ev),
        addRegistration: Boolean(ev.addRegistration),
        location: ev.location || "",
        event: ev,
      });
    }
  }
  return rows;
}

export function filterEventRows(rows, filterKey) {
  const key = filterKey || "all";
  if (key === "all") return rows;
  const now = new Date();
  return rows.filter((row) => {
    switch (key) {
      case "upcoming":
        return isEventUpcoming(row.event, now);
      case "past":
        return isEventPast(row.event, now);
      case "recurring":
        return row.recurring;
      case "published":
        return !row.hidden;
      case "draft":
        return row.hidden;
      case "registration":
        return row.addRegistration;
      default:
        return true;
    }
  });
}

export function searchEventRows(rows, query) {
  const q = String(query || "").trim().toLowerCase();
  if (!q) return rows;
  return rows.filter((row) => {
    const hay = [
      row.title,
      row.widgetName,
      row.location,
      row.startAt,
      row.endAt,
    ]
      .join(" ")
      .toLowerCase();
    return hay.includes(q);
  });
}

export function formatEventDateRange(startAt, endAt) {
  const start = parseEventDate(startAt);
  const end = parseEventDate(endAt);
  if (!start && !end) return "No date set";
  const opts = { dateStyle: "medium", timeStyle: "short" };
  if (start && end) {
    return `${start.toLocaleString("en-US", opts)} – ${end.toLocaleString("en-US", opts)}`;
  }
  if (start) return start.toLocaleString("en-US", opts);
  return end.toLocaleString("en-US", opts);
}
